import { useMemo, useState } from "react";
import { ChevronRight, FileBadge2, FilePlus2, FileText, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useT } from "@/i18n";
import { useData } from "@/context/DataContext";
import { CertificateCard } from "@/components/CertificateCard";
import { RequestStatusBadge } from "@/components/RequestStatusBadge";
import { Badge, Card, EmptyState, SectionHeading, Skeleton } from "@/components/ui";
import { certificateRequestsApi, type CustomerCertificateRequest } from "@/lib/api";
import { requestTypeLabel, validityLabel } from "@/lib/certificate-requests";
import type { CertificateStatus } from "@/lib/mock";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/format";

type Filter = "all" | "active" | "pending" | "expired";

const FILTER_MAP: Record<Filter, (s: CertificateStatus) => boolean> = {
  all: () => true,
  active: (s) => s === "active" || s === "expiring",
  pending: (s) => s === "pending",
  expired: (s) => s === "expired" || s === "revoked",
};

export function Certificates() {
  const { t } = useT();
  const { data } = useData();
  const requestsQuery = useQuery({
    queryKey: ["me", "certificate-requests"],
    queryFn: certificateRequestsApi.list,
  });
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    return data.certificates
      .filter((c) => FILTER_MAP[filter](c.status))
      .filter((c) =>
        search
          ? c.holder.toLowerCase().includes(search.toLowerCase()) ||
            c.documentNumber.includes(search) ||
            c.serial.toLowerCase().includes(search.toLowerCase())
          : true
      );
  }, [filter, search, data.certificates]);

  const filters: { id: Filter; label: string }[] = [
    { id: "all", label: t("cert.filter_all") },
    { id: "active", label: t("cert.filter_active") },
    { id: "pending", label: t("cert.filter_pending") },
    { id: "expired", label: t("cert.filter_expired") },
  ];

  return (
    <div className="space-y-8">
      <SectionHeading
        title={t("cert.title")}
        subtitle={t("cert.subtitle")}
        action={
          <Link to="/solicitar" className="btn-primary">
            <FilePlus2 className="h-4 w-4" />
            {t("nav.request")}
          </Link>
        }
      />

      <section className="space-y-3">
        <SectionHeading
          title={t("cert.requests_title")}
          subtitle={t("cert.requests_subtitle")}
        />
        {requestsQuery.isPending ? (
          <div className="grid gap-3">
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </div>
        ) : requestsQuery.isError ? (
          <Card className="p-5 text-sm text-red-700">
            {requestsQuery.error instanceof Error
              ? requestsQuery.error.message
              : t("onb.err_unknown")}
          </Card>
        ) : requestsQuery.data.requests.length === 0 ? (
          <EmptyState
            icon={<FileText className="h-6 w-6" />}
            title={t("cert.no_requests")}
            body={t("cert.no_requests_body")}
          />
        ) : (
          <div className="grid gap-3">
            {requestsQuery.data.requests.map((request) => (
              <RequestCard key={request.id} request={request} />
            ))}
          </div>
        )}
      </section>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          {filters.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={cn(
                "rounded-full px-3.5 py-1.5 text-sm font-medium transition",
                filter === f.id
                  ? "bg-slate-900 text-white"
                  : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
              )}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="relative sm:w-80">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            type="search"
            placeholder={t("common.search")}
            className="input pl-9"
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon={<FileBadge2 className="h-6 w-6" />}
          title={
            data.certificates.length === 0 ? t("dash.empty_title") : t("cert.empty_filter")
          }
          body={data.certificates.length === 0 ? t("dash.empty_body") : undefined}
          action={
            <Link to="/solicitar" className="btn-primary">
              <FilePlus2 className="h-4 w-4" />
              {t("nav.request")}
            </Link>
          }
        />
      ) : (
        <div className="grid gap-3">
          {filtered.map((c) => (
            <CertificateCard key={c.id} certificate={c} />
          ))}
        </div>
      )}
    </div>
  );
}

function RequestCard({ request }: { request: CustomerCertificateRequest }) {
  const { t, locale } = useT();
  return (
    <Link
      to={`/certificados/solicitudes/${request.id}`}
      className="card group flex items-center gap-4 p-5 transition hover:-translate-y-0.5 hover:shadow-card"
    >
      <span className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-brand-50 text-brand-700">
        <FileText className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <RequestStatusBadge status={request.status} />
          <span className="text-xs text-slate-500">
            {t("cert.request_updated_on")}: {formatDate(request.updatedAt, locale)}
          </span>
        </div>
        <p className="mt-2 truncate text-base font-semibold text-slate-900">
          {requestTypeLabel(t, request.type)}
        </p>
        <p className="mt-1 text-sm text-slate-600">
          {t("cert.class")} {request.class} · {validityLabel(t, request.validityMonths)}
        </p>
      </div>
      <Badge tone={request.artifacts.length > 0 ? "green" : "neutral"}>
        {request.artifacts.length} {t("cert.request_artifacts")}
      </Badge>
      <ChevronRight className="h-5 w-5 flex-none text-slate-400 transition group-hover:text-slate-600" />
    </Link>
  );
}
